import { createContext, useContext, useEffect, useState } from "react";
import { api, getToken, setToken, clearToken, refreshDownloadToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        clearToken();
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  // SEC-005 : dès qu'un utilisateur est authentifié, récupère un jeton de téléchargement à durée limitée.
  useEffect(() => {
    if (user && typeof user === "object") refreshDownloadToken();
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_verification) return data; // compte non vérifié : ne pas connecter
    if (data.requires_2fa) return data; // 2FA super-admin : étape 2 requise, ne pas appeler /auth/me
    if (data.access_token) setToken(data.access_token);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      return me.data;
    } catch {
      setUser(data);
      return data;
    }
  };

  const login2fa = async (challenge, code) => {
    const { data } = await api.post("/auth/login/2fa", { challenge, code });
    if (data.access_token) setToken(data.access_token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  };
  const resend2fa = async (challenge) => (await api.post("/auth/login/2fa/resend", { challenge })).data;

  const finalizeToken = async (data) => {
    if (data.access_token) {
      setToken(data.access_token);
      try { const me = await api.get("/auth/me"); setUser(me.data); } catch { /* noop */ }
    }
    return data;
  };
  const verifyEmailCode = async (email, code) =>
    finalizeToken((await api.post("/auth/register/verify-email", { email, code })).data);
  const verifyPhoneCode = async (email, code) =>
    finalizeToken((await api.post("/auth/register/verify", { email, code })).data);
  const resendEmailCode = async (email) => (await api.post("/auth/register/resend-email", { email })).data;
  const resendPhoneCode = async (email) => (await api.post("/auth/register/resend", { email })).data;
  const forgotPasswordSms = async (email) => (await api.post("/auth/forgot-password/sms", { email })).data;
  const resetPasswordSms = async (email, code, new_password) =>
    (await api.post("/auth/reset-password/sms", { email, code, new_password })).data;

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) setToken(data.access_token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  };

  const trialStart = async (payload) => (await api.post("/trial/start", payload)).data;
  const trialVerifyEmail = async (trial_id, code) => (await api.post("/trial/verify-email", { trial_id, code })).data;
  const trialVerifyPhone = async (trial_id, code) => (await api.post("/trial/verify-phone", { trial_id, code })).data;
  const trialResend = async (trial_id, channel = "both") => (await api.post("/trial/resend", { trial_id, channel })).data;
  const trialActivate = async (trial_id) => {
    const { data } = await api.post("/trial/activate", { trial_id });
    if (data.access_token) setToken(data.access_token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  };

  const refreshUser = async () => {
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout API call failed (ignored):", e?.message || e);
    }
    clearToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, login2fa, resend2fa, register, verifyEmailCode, verifyPhoneCode, resendEmailCode, resendPhoneCode, forgotPasswordSms, resetPasswordSms, trialStart, trialVerifyEmail, trialVerifyPhone, trialResend, trialActivate, refreshUser, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
